import React, { useEffect, useState } from "react";
import { X, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import TaskRouteMap from "@/components/TaskRouteMap";
import { useRouteIncidents } from "@/hooks/useRouteIncidents";

// Wide right slide-in panel that sits over the scheduler (map-style backdrop).
export function SlidePanel({ children, onClose, maxW = "max-w-5xl", testId }) {
  useEffect(() => {
    const h = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose]);
  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-slate-500/20 backdrop-blur-[1px]" onClick={onClose} data-testid={testId}>
      <div className={`relative h-full w-full ${maxW} bg-white shadow-2xl overflow-hidden animate-in slide-in-from-right duration-300 flex flex-col`} onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose}
          className="absolute right-3 top-3 z-30 flex h-8 w-8 items-center justify-center rounded-full bg-white/90 text-slate-600 shadow ring-1 ring-slate-200 hover:bg-slate-50"
          data-testid="panel-close-float">
          <X className="w-4 h-4" />
        </button>
        {children}
      </div>
    </div>
  );
}

const TL_DOT = {
  done: "bg-emerald-500 border-emerald-500 text-white",
  active: "bg-white border-emerald-500 text-emerald-600 ring-2 ring-emerald-100",
  pending: "bg-white border-slate-300 text-slate-300",
};

export function RouteMapMini({ taskId, fill }) {
  const [route, setRoute] = useState(null);
  const [err, setErr] = useState(false);
  const [showAll, setShowAll] = useState(true);
  useEffect(() => {
    if (!taskId) { setErr(true); return; }
    setErr(false); setRoute(null);
    api.get(`/tasks/${taskId}/route`).then((r) => setRoute(r.data)).catch(() => setErr(true));
  }, [taskId]);
  const { data: inc } = useRouteIncidents(route?.route?.geometry || [], {
    snapToRoads: false, showAll, enabled: !!(route && route.available && route.route?.geometry),
  });
  const boxH = fill ? "flex-1 min-h-[240px]" : "h-52";
  return (
    <div className={fill ? "p-3 flex-1 flex flex-col min-h-0" : "p-3"} data-testid="panel-route-map">
      <div className="text-xs font-bold uppercase tracking-wide text-slate-500 mb-2 shrink-0">Route</div>
      {err || (route && !route.available) ? (
        <div className={`${boxH} rounded-xl border border-slate-200 bg-slate-100 flex items-center justify-center text-xs text-slate-400 text-center px-4`}>
          {(route && route.reason) || "No route map available for this shift"}
        </div>
      ) : !route ? (
        <div className={`${boxH} rounded-xl border border-slate-200 bg-slate-50 flex items-center justify-center`}><Loader2 className="w-5 h-5 animate-spin text-indigo-400" /></div>
      ) : (
        <>
          <TaskRouteMap
            route={route}
            incidents={inc?.incidents || []}
            providers={inc?.providers}
            showAll={showAll}
            onShowAllChange={setShowAll}
            restrictRegistrations={route.vehicle_registrations}
            className={`w-full ${boxH} rounded-xl overflow-hidden border border-slate-200 relative z-0`}
          />
          {route.route && <div className="mt-2 flex gap-3 text-xs text-slate-500 shrink-0">
            <span>{route.route.distance_miles ?? route.route.distance ?? "—"} mi</span>
            <span>{route.route.duration_mins ?? route.route.duration ?? "—"} mins</span>
          </div>}
        </>
      )}
    </div>
  );
}

export function DayTimeline({ steps, vehicleTitle, onOpenDay, activeShiftId, onOpenShift, embedded }) {
  return (
    <div className={embedded ? "p-4" : "w-full sm:w-64 shrink-0 border-b sm:border-b-0 sm:border-r border-slate-100 bg-slate-50/60 p-4 overflow-y-auto"} data-testid="day-timeline">
      <button onClick={onOpenDay} className="w-full text-left group mb-3" data-testid="timeline-day-summary">
        <div className="flex items-center justify-between">
          <span className="text-xs font-bold uppercase tracking-wide text-slate-500">Day Summary</span>
          <span className="text-slate-300 group-hover:text-indigo-500">›</span>
        </div>
        <div className="text-sm font-semibold text-slate-700 truncate">{vehicleTitle}</div>
      </button>
      <ol className="relative space-y-1">
        {(steps || []).map((s, i) => {
          const tone = s.done ? "done" : (s.active ? "active" : "pending");
          const clickable = s.kind === "shift" && onOpenShift;
          return (
            <li key={i} className="relative pl-7">
              {i < (steps.length - 1) && <span className="absolute left-[11px] top-5 bottom-[-4px] w-px bg-slate-200" />}
              <span className={`absolute left-0 top-0.5 flex h-6 w-6 items-center justify-center rounded-full border text-[10px] ${TL_DOT[tone]}`}>✓</span>
              <button
                disabled={!clickable}
                onClick={() => clickable && onOpenShift(s.shift_id)}
                className={`w-full text-left rounded-lg px-2 py-1.5 -ml-1 ${s.active ? "bg-white ring-1 ring-slate-200 shadow-sm" : ""} ${clickable ? "hover:bg-white" : ""}`}
                data-testid={s.kind === "shift" ? `timeline-shift-${s.shift_id}` : `timeline-depot-${i}`}>
                <div className="text-sm font-medium text-slate-700">{s.label}</div>
                <div className={`text-xs ${s.done ? "text-emerald-600" : "text-slate-400"}`}>{s.detail}</div>
              </button>
            </li>
          );
        })}
        {(!steps || steps.length === 0) && <li className="text-xs text-slate-400">No shifts for this day.</li>}
      </ol>
    </div>
  );
}

export function ResourceChip({ name, kind, job_title, to }) {
  const cls = "inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-2.5 py-1 text-xs text-slate-700";
  const inner = (
    <>
      <span className={`inline-block h-2 w-2 rounded-full ${kind === "user" ? "bg-rose-400" : "bg-slate-400"}`} />
      <span className="font-medium">{name}</span>
      {kind === "user" && job_title ? <span className="text-slate-400">· {job_title}</span> : null}
    </>
  );
  if (to) return <Link to={to} className={`${cls} hover:border-indigo-300 hover:text-indigo-700 transition-colors`} data-testid="resource-chip">{inner}</Link>;
  return <span className={cls} data-testid="resource-chip">{inner}</span>;
}
